import { db } from "@app/firebase"
import { doc, getDoc } from "firebase/firestore"
import { 
  FirestoreCollections, 
  TestAdministration, 
  TestAdministrationSettings,
  TestAdministrationTimingOptions,
  TestAdministrationTimingMultipliers 
} from "../types"

export const getTestAdministrationById = async (id: string): Promise<TestAdministration | null> => {
  try {
    const docRef = doc(db, FirestoreCollections.testAdministrations, id)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
      return docSnap.data() as TestAdministration
    }

    return null
  } catch (e) {
    console.error('Cannot get test administration data: ', e)
    return null
  }
}

export const calculateAdjustedTimeLimit = (
  baseTimeLimit: number,
  settings?: TestAdministrationSettings
): number => {
  if (!settings?.timing) {
    return baseTimeLimit
  }

  const roundToMinutes = (seconds: number) => Math.ceil(seconds / 60) * 60

  switch (settings.timing) {
    case TestAdministrationTimingOptions.UNTIMED:
      return 0 // 0 indicates unlimited time in the system
    
    case TestAdministrationTimingOptions.STANDARD:
      return baseTimeLimit
    
    case TestAdministrationTimingOptions.TIME_AND_A_HALF:
      return roundToMinutes(baseTimeLimit * TestAdministrationTimingMultipliers.TIME_AND_A_HALF)

    case TestAdministrationTimingOptions.DOUBLE:
      return Math.ceil(baseTimeLimit * TestAdministrationTimingMultipliers.DOUBLE)

    case TestAdministrationTimingOptions.CUSTOM:
      if (settings.customTimePercentage) {
        return roundToMinutes(baseTimeLimit * (settings.customTimePercentage / 100))
      }
      return baseTimeLimit
    
    default:
      return baseTimeLimit
  }
}

export const calculateMaximumAdjustedTimeLimit = (
  baseTimeLimit: number,
  testAdminSettings?: TestAdministrationSettings,
  studentSettings?: TestAdministrationSettings
): number => {
  if (!baseTimeLimit) return 0
  // Calculate time with test admin settings
  const testAdminTime = testAdminSettings 
    ? calculateAdjustedTimeLimit(baseTimeLimit, testAdminSettings)
    : baseTimeLimit

  // Calculate time with student settings
  const studentTime = studentSettings
    ? calculateAdjustedTimeLimit(baseTimeLimit, studentSettings)
    : baseTimeLimit

  // If either is unlimited (0), return unlimited
  if (testAdminTime === 0 || studentTime === 0) {
    return 0
  }

  // NEW PRIORITY LOGIC:
  // Check if student has an EFFECTIVE accommodation (>100% of base time)
  const hasEffectiveAccommodation = studentTime > baseTimeLimit

  if (hasEffectiveAccommodation) {
    // Student has effective accommodation (>100%)
    // Use student time unless test admin provides MORE time
    // This protects students with accommodations from having their time reduced
    return Math.max(studentTime, testAdminTime)
  } else {
    // Student has no effective accommodation (time is 100% or less of base)
    // Use test admin time (which can be <100% to reduce time)
    return testAdminTime
  }
}